import { BankOutlined, BuildOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "style-guide/component/Typography";

interface SectorOptionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
}

const SectorCard: React.FC<SectorOptionProps> = ({
  icon,
  title,
  description,
  onClick,
}) => (
  <div
    className="w-full sm:w-[250px] md:w-[350px] border-[1px] border-[#D9D9D9] gap-4 md:gap-10 rounded-lg p-6 flex flex-col justify-between hover:border-blue-primary cursor-pointer transition-all duration-200 hover:shadow-lg"
    onClick={onClick}
  >
    <div className="flex flex-col gap-4">
      <div className="flex justify-center">{icon}</div>
      <div className="text-center">
        <Typography
          variant="LG"
          text={title}
          className="font-semibold text-blue-primary mb-2"
        />
        <Typography
          variant="SM"
          text={description}
          className="text-gray-600 leading-relaxed"
        />
      </div>
    </div>
  </div>
);

const SectorSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localStorage.clear();
    document.cookie = "rfp_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  },[])

  const getRedirectUrl = () => {
    if (location.search.includes("redirectUrl=")) {
      const urlParams = new URLSearchParams(location.search);
      return urlParams.get("redirectUrl") || "/";
    }
    return "/";
  };

  const handleSectorSelection = (sector: "PUBLIC" | "PRIVATE") => {
    // Store the selected sector
    localStorage.setItem("userSector", sector);

    // Navigate to the intended destination
    const redirectUrl = getRedirectUrl();
    navigate(redirectUrl, { replace: true });
  };

  const sectorOptions: SectorOptionProps[] = [
    {
      icon: <BankOutlined style={{ fontSize: "48px", color: "#0F428D" }} />,
      title: "PUBLIC SECTOR",
      description:
        "Government agencies, public institutions, NGOs, educational institutions, and other public organizations",
      onClick: () => handleSectorSelection("PUBLIC"),
    },
    {
      icon: <BuildOutlined style={{ fontSize: "48px", color: "#0F428D" }} />,
      title: "PRIVATE SECTOR",
      description:
        "Corporations, private companies, startups, consulting firms, and other private organizations",
      onClick: () => handleSectorSelection("PRIVATE"),
    },
  ];

  return (
    <div className="w-full p-4 sm:p-6 md:p-12 font-helvetica-neue flex flex-col gap-8 sm:gap-8">
      <div className="bg-pink gap-2 flex flex-col justify-between rounded-md p-6">
        <Typography
          text="Select Your Sector"
          variant="Heading 3"
          className="text-black text-lg sm:text-xl md:text-2xl"
        />
        <Typography
          text="Choose the sector that best describes your organization to help us tailor your experience."
          variant="LG"
          className="text-black"
        />
      </div>

      <div className="w-full flex flex-col md:flex-row gap-6 items-center md:items-stretch justify-center">
        {sectorOptions.map((option, index) => (
          <SectorCard
            key={index}
            icon={option.icon}
            title={option.title}
            description={option.description}
            onClick={option.onClick}
          />
        ))}
      </div>

      <div className="text-center">
        <Typography
          text="Your selection will help us provide more relevant features and recommendations."
          variant="SM"
          className="text-gray-500"
        />
      </div>
    </div>
  );
};

export default SectorSelectionPage;
